import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';

import { TimeSlotDict, tokenRequests } from '../api/api';
import cacheQueryKeys from '../resources/queryCacheKeys';
import { useToastController } from '../toast';
import { statusCodesMapper } from '../utils/exception';

export type ServicePayload = { uuid: string; quantity: number };
export type PricingFormsPayload = {
  uuid: string;
};

type Props = {
  token: string;
  services?: ServicePayload[];
  pricingForms?: PricingFormsPayload[];
  onSuccess?: (data?: TimeSlotDict) => void;
};

const formatDay = (day: Date) => moment(day).format('YYYY-MM-DD');

const DEFAULT_WEEKS = 4;
const MAX_WEEKS_LIMIT = 12;

export const BEGGINING_DATE = new Date();
BEGGINING_DATE.setHours(0, 0, 0, 0);

export const TO_DATE_INIT = new Date(BEGGINING_DATE.getTime() + DEFAULT_WEEKS * 7 * 24 * 60 * 60 * 1000);
TO_DATE_INIT.setHours(0, 0, 0, 0);

export const MAX_DATE = new Date(Date.now() + MAX_WEEKS_LIMIT * 7 * 24 * 60 * 60 * 1000);
MAX_DATE.setHours(0, 0, 0, 0);

export const useTimeSlotsV2 = ({ token, onSuccess, pricingForms, services }: Props) => {
  const { showToast } = useToastController();
  const queryClient = useQueryClient();
  const [fromDate, setFromDate] = useState<Date>(new Date(BEGGINING_DATE));
  const [toDate, setToDate] = useState<Date>(TO_DATE_INIT);
  const increaseTimespanByWeeks = (addedWeeks: number) => {
    if (toDate >= MAX_DATE) {
      return;
    }

    const newFromDate = new Date(toDate);
    newFromDate.setDate(newFromDate.getDate() + 1);
    setFromDate(newFromDate);

    const newToDate = new Date(toDate);
    newToDate.setDate(newToDate.getDate() + addedWeeks * 7);
    setToDate(newToDate >= MAX_DATE ? MAX_DATE : newToDate);
  };

  const increaseTimespanToFullMonth = () => {
    if (toDate >= MAX_DATE) {
      return;
    }

    const newFromDate = new Date(toDate);
    newFromDate.setDate(newFromDate.getDate() + 1);
    setFromDate(newFromDate);

    const newToDate = new Date(toDate.getFullYear(), toDate.getMonth() + 1, 1);
    setToDate(newToDate >= MAX_DATE ? MAX_DATE : newToDate);
  };

  const query = useQuery(
    [cacheQueryKeys.OnlineBookingTimeSlots, toDate],
    async () => {
      const response = await tokenRequests.fetchTimeSlotsV3(
        formatDay(fromDate),
        formatDay(toDate),
        services,
        pricingForms,
        token
      );
      return statusCodesMapper(response, {
        unhandled: () => {
          showToast('Something went wrong, please try again.');
          throw new Error();
        },
        success: (data) => {
          const previousData = queryClient.getQueryData<TimeSlotDict>([cacheQueryKeys.OnlineBookingTimeSlots]) || {};
          const newData = Object.fromEntries(data.map((day) => [day.date, day.time_slots]));
          let result = {};
          if (fromDate.getTime() === BEGGINING_DATE.getTime()) {
            result = { ...newData };
          } else {
            result = { ...previousData, ...newData };
          }
          queryClient.setQueryData([cacheQueryKeys.OnlineBookingTimeSlots], result);
          return result;
        },
      });
    },
    {
      staleTime: 0,
      onSuccess,
    }
  );

  return {
    ...query,
    data: queryClient.getQueryData<TimeSlotDict>([cacheQueryKeys.OnlineBookingTimeSlots]),
    increaseTimespanByWeeks,
    increaseTimespanToFullMonth,
    period: { begginingDate: BEGGINING_DATE, fromDate, toDate, limitReached: toDate >= MAX_DATE, maxDate: MAX_DATE },
  };
};
